import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { control } from "./styles";

/**
 * A select the platform can style.
 *
 * ## Why this one is rebuilt when the others are not
 *
 * Every other control here is the native element with CSS on it, because the
 * native behaviour is worth more than the appearance. `<select>` is the
 * exception: its dropdown is drawn by the operating system and cannot be
 * styled at all, so the one control that most obviously looks foreign is the
 * one that cannot be fixed by styling.
 *
 * ## What that costs, and what is paid back
 *
 * Everything a native select does for free has to be rebuilt: the roles,
 * keyboard navigation, typeahead, focus return, closing on outside clicks. All
 * of it is here, and the point of it being here is that it is written once
 * rather than badly in seven places.
 *
 * `role="combobox"` with `role="listbox"` and `role="option"` is the documented
 * pattern, so screen readers announce it as a select and `getByRole` still
 * finds it.
 *
 * ## The list is portalled, into the dialog when there is one
 *
 * Rendered out of the flow and positioned from the trigger's rectangle, because
 * the alternative is being clipped: these sit inside scrolling panels, and one
 * ancestor with `overflow: hidden` - or `backdrop-filter`, which quietly makes
 * a containing block - is enough to cut the options in half.
 *
 * Where it goes matters. Dialogs here are real `<dialog>` elements opened with
 * `showModal()`, and a modal dialog makes *everything outside it* `inert` - so
 * a list portalled to the end of the body was painted, in the right place, and
 * completely untouchable. Clicks fell through it to the form underneath and the
 * control looked dead.
 *
 * The top layer is not the answer either: `popover` does promote it above the
 * dialog, and it is still outside the dialog, so it is still inert. That was
 * tried, and it looked like it worked - the list was visible - which is the
 * worst kind of nearly-right.
 *
 * So the list goes *inside* the dialog when the trigger is in one, and to the
 * body when it is not.
 */

/**
 * Where the list goes, given the trigger it belongs to.
 *
 * ## Compact triggers do not get a compact list
 *
 * A chevron-only trigger is about twenty pixels wide, and matching the list to
 * it produced a column of vertical slivers with a highlight sliding up and down
 * it - unreadable, and unmistakably broken. So a narrow trigger gets a list
 * sized for its contents instead, aligned to the trigger's right edge because
 * that is the edge it was next to.
 *
 * Either way the result is nudged back inside the window, since a list that
 * starts near the right edge would otherwise hang off it.
 */
const COMPACT_WIDTH = 240;

function geometry(rect: DOMRect, compact: boolean) {
  const width = compact ? Math.max(COMPACT_WIDTH, rect.width) : rect.width;
  const preferred = compact ? rect.right - width : rect.left;
  const left = Math.max(8, Math.min(preferred, window.innerWidth - width - 8));

  return {
    left,
    top: rect.bottom + 4,
    width,
    right: "auto" as const,
    bottom: "auto" as const,
    margin: 0,
  };
}

export interface Option<T extends string = string> {
  value: T;
  label: string;
  /** Shown under the label, for options that need a word of explanation. */
  hint?: string;
  disabled?: boolean;
}

export function Select<T extends string = string>({
  value,
  options,
  onChange,
  placeholder = "Select…",
  invalid = false,
  disabled = false,
  compact = false,
  className = "",
  "aria-label": ariaLabel,
  id: providedId,
  ...rest
}: {
  value: T | "";
  options: Option<T>[];
  onChange: (value: T) => void;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  /**
   * Chevron only, with no room for a label.
   *
   * For the places where the control sits inside something that has already
   * said what it is - a device strip whose button is named "Camera", where a
   * second "Camera" in the trigger would be the same word twice. The list is
   * unchanged; only the trigger shrinks.
   */
  compact?: boolean;
  className?: string;
  "aria-label"?: string;
  id?: string;
} & Record<`data-${string}`, unknown>) {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const listId = `${id}-list`;

  const trigger = useRef<HTMLButtonElement | null>(null);
  const list = useRef<HTMLUListElement | null>(null);

  /**
   * Where the list is rendered: the nearest open dialog, or the body.
   *
   * Read on every render rather than cached, because the same control can be
   * used inside a dialog and outside one, and a stale container would put the
   * list somewhere inert.
   */
  const host =
    trigger.current?.closest("dialog") ??
    (typeof document === "undefined" ? null : document.body);

  const [open, setOpen] = useState(false);
  /** Which option the keyboard is on, which is not the same as the chosen one. */
  const [active, setActive] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const selected = options.find((option) => option.value === value) ?? null;

  const place = () => {
    const box = trigger.current?.getBoundingClientRect();

    if (box) setRect(box);
  };

  /*
   * Measured before paint, so the list never appears in the wrong place first.
   */
  useLayoutEffect(() => {
    if (open) place();
  }, [open]);

  /*
   * Followed while it is open.
   *
   * A fixed-position list does not move with a scrolling ancestor, so without
   * this it detaches from the control the moment anything scrolls - which in a
   * console panel is immediately. Closing on scroll would be simpler and is
   * what makes native selects on some platforms feel broken.
   */
  useEffect(() => {
    if (!open) return;

    const follow = () => place();

    window.addEventListener("scroll", follow, true);
    window.addEventListener("resize", follow);

    return () => {
      window.removeEventListener("scroll", follow, true);
      window.removeEventListener("resize", follow);
    };
  }, [open]);

  /* Outside clicks close it, the way every menu on the platform behaves. */
  useEffect(() => {
    if (!open) return;

    const away = (event: PointerEvent) => {
      const target = event.target as Node;

      if (trigger.current?.contains(target) || list.current?.contains(target)) {
        return;
      }

      setOpen(false);
    };

    document.addEventListener("pointerdown", away);

    return () => document.removeEventListener("pointerdown", away);
  }, [open]);

  const openAt = (index: number) => {
    if (disabled) return;

    setActive(Math.max(0, index));
    setOpen(true);
  };

  const choose = (option: Option<T>) => {
    if (option.disabled) return;

    onChange(option.value);
    setOpen(false);
    /*
     * Focus goes back to the trigger, always.
     *
     * The list is in a portal at the end of the body, so leaving focus there
     * puts the next Tab at the end of the document - miles from the form the
     * person is filling in.
     */
    trigger.current?.focus();
  };

  /*
   * Typeahead, because a native select has it and its absence is the thing that
   * makes a rebuilt one feel wrong. Typing "un" on a visibility list should land
   * on Unlisted rather than opening a search box.
   */
  const typed = useRef({ text: "", at: 0 });

  const seek = (char: string) => {
    const now = Date.now();

    typed.current = {
      text: now - typed.current.at > 700 ? char : typed.current.text + char,
      at: now,
    };

    const found = options.findIndex((option) =>
      option.label.toLowerCase().startsWith(typed.current.text.toLowerCase()),
    );

    if (found >= 0) {
      setActive(found);

      if (!open) onChange(options[found]!.value);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const step = (delta: number) => {
      event.preventDefault();

      if (!open) {
        openAt(options.findIndex((o) => o.value === value));

        return;
      }

      // Skips disabled entries, so holding a key cannot park on one.
      let next = active;

      for (let i = 0; i < options.length; i += 1) {
        next = (next + delta + options.length) % options.length;

        if (!options[next]?.disabled) break;
      }

      setActive(next);
    };

    switch (event.key) {
      case "ArrowDown":
        step(1);
        break;
      case "ArrowUp":
        step(-1);
        break;
      case "Home":
        event.preventDefault();
        setActive(0);
        break;
      case "End":
        event.preventDefault();
        setActive(options.length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();

        if (!open) openAt(options.findIndex((o) => o.value === value));
        else if (options[active]) choose(options[active]);

        break;
      case "Escape":
        if (open) {
          event.preventDefault();
          setOpen(false);
        }

        break;
      case "Tab":
        setOpen(false);
        break;
      default:
        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey) {
          seek(event.key);
        }
    }
  };

  return (
    <>
      <button
        ref={trigger}
        id={id}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openAt(options.findIndex((o) => o.value === value)))}
        onKeyDown={onKeyDown}
        className={
          compact
            ? `flex h-full shrink-0 items-center px-2 text-faint outline-none transition-colors hover:text-ink disabled:opacity-50 ${className}`
            : control(
                invalid,
                `flex items-center justify-between gap-2 text-left ${className}`,
              )
        }
        {...rest}
      >
        {!compact && (
          <span className={`truncate ${selected ? "text-ink" : "text-faint"}`}>
            {selected?.label ?? placeholder}
          </span>
        )}

        <span
          aria-hidden
          className={`shrink-0 font-mono text-[9px] transition-transform ${
            compact ? "" : "text-faint"
          } ${open ? "rotate-180" : ""}`}
        >
          ▼
        </span>
      </button>

      {open &&
        rect &&
        host &&
        createPortal(
          <ul
            ref={list}
            id={listId}
            role="listbox"
            aria-activedescendant={`${id}-option-${active}`}
            tabIndex={-1}
            style={{
              position: "fixed",
              ...geometry(rect, compact),
              /*
               * Enough for the list not to run off the bottom of the window,
               * and its own scroller for when there are more options than that.
               */
              maxHeight: Math.max(160, window.innerHeight - rect.bottom - 24),
            }}
            className="z-50 overflow-y-auto rounded-[var(--radius-control)] border border-line-lit bg-raised py-1 shadow-2xl"
          >
            {options.map((option, i) => (
              <li
                key={option.value}
                id={`${id}-option-${i}`}
                role="option"
                aria-selected={option.value === value}
                aria-disabled={option.disabled || undefined}
                data-select-option={option.value}
                onPointerDown={(event) => {
                  // Or the trigger loses focus before the click resolves and
                  // the outside-click handler closes the list underneath it.
                  event.preventDefault();
                  choose(option);
                }}
                onPointerEnter={() => !option.disabled && setActive(i)}
                className={`cursor-pointer px-3 py-2 text-sm ${
                  option.disabled
                    ? "cursor-not-allowed text-ghost"
                    : i === active
                      ? "bg-accent text-accent-ink"
                      : "text-ink"
                }`}
              >
                <span className="block truncate">{option.label}</span>

                {option.hint && (
                  <span
                    className={`block truncate text-xs ${
                      i === active ? "text-accent-ink/75" : "text-faint"
                    }`}
                  >
                    {option.hint}
                  </span>
                )}
              </li>
            ))}
          </ul>,
          host,
        )}
    </>
  );
}
